import {Card, Form} from 'platform/components';
import {Box, Hide, HStack, Integer, Show} from 'platform/foundation';

import {useCallback, useMemo} from 'react';
import {Outlet} from 'react-router-dom';

import {isNil} from 'ramda';

import {suffixTestId, TestIdProps, useDebouncedCallback, useQueryState} from 'shared/web';

import {FilterContextType, FilterProvider} from '../../hooks/FilterContext';
import {useSelectedFilters} from '../../hooks/SelectedFiltersContext';
import {useHeaderHeight} from '../../hooks/useHeaderHeight';
import {usePriceReport} from '../../hooks/usePriceReport';
import {usePriceReportSearchParams} from '../../hooks/usePriceReportSearchParams';
import {FilterFormData} from '../../types/FilterFormData';
import {getDefaultValues} from '../../utils/getDefaultValues';
import {QueryStateKeys} from '../../utils/routing';
import {validateFilterValues} from '../../utils/validateFilterValues';
import {Filter} from '../Filter/Filter';
import {formValuesToRequestValues} from '../Filter/utils/formValuesToRequestValues';
import {StickyContainer} from '../StickyContainer/StickyContainer';
import {FilterModal} from './components/FilterModal';

export function FilterLayout(props: TestIdProps) {
  const {priceReport} = usePriceReport();
  const {selectedFilters, onSelectFilters} = useSelectedFilters();
  const headerHeight = useHeaderHeight();
  const {resetPage} = usePriceReportSearchParams();
  const [, , clearDebug] = useQueryState(QueryStateKeys.Debug);

  const changeHandler = useCallback(
    (context: FilterContextType, values: FilterFormData) => {
      const errors = validateFilterValues(values);
      context.setErrors(errors);

      if (isNil(errors)) {
        context.onFilterChange(formValuesToRequestValues(values));
        onSelectFilters(values);
        resetPage();
      }
    },
    [resetPage, onSelectFilters]
  );

  const debouncedChangeHandler = useDebouncedCallback(changeHandler, 500);

  const defaultValues = useMemo(() => getDefaultValues(priceReport), [priceReport]);

  if (!priceReport) {
    return null;
  }

  return (
    <FilterProvider initialFilter={formValuesToRequestValues(selectedFilters ?? defaultValues)}>
      {(context) => (
        <Form<FilterFormData>
          defaultValues={selectedFilters ?? defaultValues}
          onChange={(values) => {
            debouncedChangeHandler(context, values);
          }}
        >
          {(control, formApi) => {
            const onReset = () => {
              formApi.reset(defaultValues);
              context.onFilterChange(formValuesToRequestValues(defaultValues));
              context.setErrors(null);
              onSelectFilters(null);
              clearDebug();
            };

            return (
              <>
                <HStack>
                  <Hide onMobile onTablet>
                    <Box width={80} flexShrink={0} padding={2} paddingRight={0}>
                      <StickyContainer offset={2}>
                        <Card>
                          <Filter
                            priceReport={priceReport}
                            formControl={control}
                            formApi={formApi}
                            onReset={onReset}
                            data-testid="filter"
                          />
                        </Card>
                      </StickyContainer>
                    </Box>
                  </Hide>
                  <Box width="100%">
                    <Box
                      flexGrow={1}
                      position={[undefined, undefined, 'sticky']}
                      top={(headerHeight / 4) as Integer}
                      data-testid={suffixTestId('stickyContainer', props)}
                    >
                      <Outlet />
                    </Box>
                  </Box>
                </HStack>
                <Show onMobile onTablet>
                  <FilterModal
                    priceReport={priceReport}
                    formControl={control}
                    formApi={formApi}
                    onReset={onReset}
                    data-testid="filterModal"
                  />
                </Show>
              </>
            );
          }}
        </Form>
      )}
    </FilterProvider>
  );
}
